<template>
  <v-card>
    <v-card-title class="pb-0">
      CI-Übersicht
      <v-spacer />
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Suchen…"
        outlined
        dense
        hide-details
        single-line
        clearable
        style="max-width: 280px;"
      />
    </v-card-title>
    <v-card-subtitle class="pt-3 pb-2">
      <div class="d-flex align-center flex-wrap" style="gap: 6px;">
        <v-chip
          v-for="f in revisionFilters"
          :key="f.value"
          small
          :color="activeFilter === f.value ? f.color : ''"
          :outlined="activeFilter !== f.value"
          :text-color="activeFilter === f.value && f.dark ? 'white' : ''"
          @click="activeFilter = f.value"
        >
          <v-icon v-if="f.icon" left x-small>{{ f.icon }}</v-icon>
          {{ f.label }}
          <span class="ml-1 font-weight-bold">{{ f.count }}</span>
        </v-chip>
        <v-spacer />
        <v-select
          v-model="typeFilter"
          :items="typeOptions"
          item-text="label"
          item-value="type"
          placeholder="Alle Typen"
          outlined
          dense
          hide-details
          clearable
          multiple
          style="max-width: 240px;"
        >
          <template #selection="{ item, index }">
            <v-chip v-if="index < 1" x-small label>
              <v-icon x-small left>{{ item.icon }}</v-icon>
              {{ item.label }}
            </v-chip>
            <span v-else-if="index === 1" class="text-caption grey--text ml-1">
              +{{ typeFilter.length - 1 }}
            </span>
          </template>
          <template #item="{ item }">
            <v-icon left small>{{ item.icon }}</v-icon>
            {{ item.label }}
          </template>
        </v-select>
      </div>
    </v-card-subtitle>
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :search="search"
      :custom-filter="customFilter"
      :items-per-page.sync="itemsPerPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :footer-props="{
        'items-per-page-options': [10, 15, 25, 50, -1],
        'items-per-page-text': 'Pro Seite'
      }"
      dense
      class="ci-overview-table"
      no-data-text="Keine CIs gefunden."
      no-results-text="Keine Treffer für die Suche."
      @click:row="onRowClick"
    >
      <template #item.status="{ item }">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              :color="statusMeta(item).color"
              small
              v-bind="attrs"
              v-on="on"
            >{{ statusMeta(item).icon }}</v-icon>
          </template>
          <span>{{ statusMeta(item).label }}</span>
        </v-tooltip>
      </template>
      <template #item.name="{ item }">
        <span class="font-weight-medium">{{ item.name }}</span>
      </template>
      <template #item.type="{ item }">
        <v-chip x-small label outlined>
          <v-icon x-small left>{{ typeMeta(item).icon }}</v-icon>
          {{ typeMeta(item).label }}
        </v-chip>
      </template>
      <template #item.revision_at="{ item }">
        <span v-if="item.revision_at" class="d-flex align-center">
          <v-icon v-if="revIcon(item.revision_at)" :color="revColor(item.revision_at)" x-small class="mr-1">{{ revIcon(item.revision_at) }}</v-icon>
          <span :class="revColor(item.revision_at) + '--text'">{{ formatDate(item.revision_at) }}</span>
          <span class="text-caption grey--text ml-1">({{ revRelative(item.revision_at) }})</span>
        </span>
        <span v-else class="grey--text">—</span>
      </template>
      <template #item.updated_at="{ item }">
        <span class="text-caption grey--text">{{ formatDateTime(item.updated_at) }}</span>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import dayjs from "dayjs"
import { CI_TYPES_META, getCITypeMeta, getCIStatusMeta } from "@/utils/ci_meta"

export default {
  name: "CIOverview",
  data: () => ({
    search: "",
    activeFilter: "all",
    typeFilter: [],
    sortBy: "revision_at",
    sortDesc: false,
    itemsPerPage: 15,
  }),
  computed: {
    typeOptions(){
      return Object.entries(CI_TYPES_META).map(([type, meta]) => ({ type, label: meta.label, icon: meta.icon }))
    },
    headers(){
      return [
        { text: "", value: "status", width: 36, sortable: false, filterable: false },
        { text: "Name", value: "name" },
        { text: "Typ", value: "type", filterable: false },
        { text: "Revision", value: "revision_at", filterable: false },
        { text: "Aktualisiert", value: "updated_at", filterable: false },
      ]
    },
    allItems(){
      return this.$store.getters.get_cis_without_deleted
    },
    revisionFilters(){
      const all = this.allItems
      const today = dayjs().startOf("day")
      const soonLimit = today.add(30, "day")
      const overdue = all.filter(c => c.revision_at && dayjs(c.revision_at).isBefore(today))
      const soon = all.filter(c => c.revision_at && !dayjs(c.revision_at).isBefore(today) && dayjs(c.revision_at).isBefore(soonLimit.add(1, "day")))
      const none = all.filter(c => !c.revision_at)
      return [
        { value: "all",     label: "Alle",          count: all.length,     color: "primary",         dark: true },
        { value: "overdue", label: "Überfällig",    count: overdue.length, color: "red",             dark: true,  icon: "mdi-alert" },
        { value: "soon",    label: "Bald fällig",   count: soon.length,    color: "orange darken-1", dark: true,  icon: "mdi-clock-alert-outline" },
        { value: "none",    label: "Ohne Revision", count: none.length,    color: "grey",            dark: true,  icon: "mdi-calendar-blank" },
      ]
    },
    filteredItems(){
      let items = this.allItems
      const today = dayjs().startOf("day")
      const soonLimit = today.add(30, "day")
      if (this.activeFilter === "overdue") {
        items = items.filter(c => c.revision_at && dayjs(c.revision_at).isBefore(today))
      } else if (this.activeFilter === "soon") {
        items = items.filter(c => c.revision_at && !dayjs(c.revision_at).isBefore(today) && dayjs(c.revision_at).isBefore(soonLimit.add(1, "day")))
      } else if (this.activeFilter === "none") {
        items = items.filter(c => !c.revision_at)
      }
      if (this.typeFilter && this.typeFilter.length) {
        items = items.filter(c => this.typeFilter.includes(c.type))
      }
      return items
    },
  },
  methods: {
    typeMeta(item){
      return getCITypeMeta(item.type) || { label: item.type || "—", icon: "mdi-help-circle-outline" }
    },
    statusMeta(item){
      return getCIStatusMeta(item.status)
    },
    onRowClick(item){
      this.$emit("select-ci", item.id)
    },
    customFilter(value, search, item){
      if (!search) return true
      const s = search.toString().toLowerCase()
      const typeLabel = (getCITypeMeta(item.type) || {}).label || item.type || ""
      const statusLabel = getCIStatusMeta(item.status).label
      return [item.name, typeLabel, statusLabel].some(v => v && String(v).toLowerCase().includes(s))
    },
    revColor(d){
      if (!d) return "grey"
      const date = dayjs(d)
      const today = dayjs().startOf("day")
      if (date.isBefore(today)) return "red"
      if (date.isBefore(today.add(30, "day"))) return "orange"
      return "green"
    },
    revIcon(d){
      if (!d) return null
      const date = dayjs(d)
      const today = dayjs().startOf("day")
      if (date.isBefore(today)) return "mdi-alert"
      if (date.isBefore(today.add(30, "day"))) return "mdi-clock-alert-outline"
      return "mdi-check"
    },
    revRelative(d){
      if (!d) return ""
      const date = dayjs(d).startOf("day")
      const today = dayjs().startOf("day")
      const diff = date.diff(today, "day")
      if (diff === 0) return "heute"
      if (diff === 1) return "morgen"
      if (diff === -1) return "gestern"
      if (diff > 0) return `in ${diff} Tagen`
      return `vor ${-diff} Tagen`
    },
    formatDate(d){
      if (!d) return ""
      return dayjs(d).format("DD.MM.YYYY")
    },
    formatDateTime(d){
      if (!d) return ""
      return dayjs(d).format("DD.MM.YYYY, HH:mm")
    },
  },
}
</script>

<style scoped>
.ci-overview-table >>> tbody tr {
  cursor: pointer;
}
.ci-overview-table >>> tbody tr:hover {
  background: rgba(123, 0, 0, 0.04) !important;
}
</style>
